import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchListings, fetchStats } from "../lib/api";
import VideoCarousel from "../components/VideoCarousel";
import ListingCard from "../components/ListingCard";
import Seo from "../components/Seo";
import { Search, ShoppingBag, Briefcase, TrendingUp, Calculator, Lightbulb, Sparkles, ArrowRight, ShieldCheck, MessageCircle, Heart } from "lucide-react";

export default function HomePage() {
  const [q, setQ] = useState("");
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchListings({ has_video: true, limit: 8 }).then(setVideos).catch(() => {});
    fetchListings({ featured: true, limit: 6 }).then(setFeatured).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(q ? `/marketplace?q=${encodeURIComponent(q)}` : "/marketplace");
  };

  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        title="DealVest — Buy a Business, Sell, Raise Capital | Africa & Middle East"
        description="The easiest way to buy a business, sell yours, raise capital and find investors across Africa and the Middle East. AI-powered, mobile-first, built by entrepreneurs."
        path="/"
      />
      {/* HERO */}
      <section className="relative pt-12 pb-10 md:pt-20 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] rounded-full bg-blue-100/60 blur-3xl opacity-70" />
          <div className="absolute top-40 left-[5%] w-[400px] h-[400px] rounded-full bg-cyan-100/50 blur-3xl" />
        </div>
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 text-center dv-fade-up">
          <span className="dv-pill mx-auto mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Trusted across Africa & Middle East
          </span>
          <h1 className="dv-display dv-h1 mb-5">
            Buy, Sell & Raise Capital<br />
            <span className="text-blue-600">in one click.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-10">
            The simplest place to buy a business, sell yours, find investors, or get a free valuation.
          </p>

          {/* SEARCH BAR */}
          <form onSubmit={onSearch} className="dv-search max-w-2xl mx-auto p-2 flex items-center gap-2" data-testid="hero-search-form">
            <div className="flex-1 flex items-center gap-3 px-3">
              <Search size={20} className="text-slate-400 flex-shrink-0" />
              <input
                type="text"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search businesses, industries, countries..."
                className="flex-1 bg-transparent border-none outline-none text-base py-3 placeholder:text-slate-400"
                data-testid="hero-search-input"
              />
            </div>
            <button type="submit" className="dv-btn dv-btn-primary h-12 px-5" data-testid="hero-search-btn">
              <Search size={16} /> <span className="hidden sm:inline">Search</span>
            </button>
          </form>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-5 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-500" /> Verified Listings</span>
            <span>·</span>
            <span>{stats.businesses_listed || "1,200+"} businesses</span>
            <span>·</span>
            <span>{stats.investors_registered || "800+"} investors</span>
          </div>
        </div>
      </section>

      {/* PRIMARY ACTIONS */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mt-2 md:mt-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/marketplace" className="dv-action-tile is-primary" data-testid="action-buy-business">
            <div className="icon-wrap"><ShoppingBag size={22} /></div>
            <div>
              <h3 className="text-xl font-bold mb-1">Buy a Business</h3>
              <p className="text-sm muted">Browse vetted businesses for sale</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto" />
          </Link>
          <Link to="/dashboard" className="dv-action-tile" data-testid="action-sell-business">
            <div className="icon-wrap"><Briefcase size={22} /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Sell a Business</h3>
              <p className="text-sm text-slate-500">List your business in 5 minutes</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto text-slate-400" />
          </Link>
          <Link to="/dashboard" className="dv-action-tile" data-testid="action-raise-capital">
            <div className="icon-wrap"><TrendingUp size={22} /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Raise Capital</h3>
              <p className="text-sm text-slate-500">Match with active investors</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto text-slate-400" />
          </Link>
          <Link to="/valuation" className="dv-action-tile" data-testid="action-free-valuation">
            <div className="icon-wrap"><Calculator size={22} /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Free Valuation</h3>
              <p className="text-sm text-slate-500">Voice, photos, or form</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto text-slate-400" />
          </Link>
          <Link to="/idea-evaluator" className="dv-action-tile" data-testid="action-idea-evaluator">
            <div className="icon-wrap"><Lightbulb size={22} /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Evaluate an Idea</h3>
              <p className="text-sm text-slate-500">Speak it. AI plans it.</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto text-slate-400" />
          </Link>
          <Link to="/matches" className="dv-action-tile" data-testid="action-matches">
            <div className="icon-wrap"><Sparkles size={22} /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Find Matches</h3>
              <p className="text-sm text-slate-500">AI investor & buyer matching</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto text-slate-400" />
          </Link>
          <Link to="/whatsapp" className="dv-action-tile" data-testid="action-whatsapp">
            <div className="icon-wrap" style={{ background: "#D1FAE5", color: "#059669" }}><MessageCircle size={22} /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">On WhatsApp</h3>
              <p className="text-sm text-slate-500">Coming soon</p>
            </div>
            <ArrowRight size={18} className="ml-auto mt-auto text-slate-400" />
          </Link>
        </div>
      </section>

      {/* PITCH VIDEOS */}
      <VideoCarousel listings={videos} />

      {/* FEATURED LISTINGS */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <span className="dv-eyebrow">Featured</span>
            <h2 className="dv-display dv-h2 mt-2">Handpicked opportunities.</h2>
          </div>
          <Link to="/marketplace" className="dv-btn dv-btn-secondary dv-btn-sm" data-testid="view-all-listings-btn">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-listings">
          {featured.slice(0, 6).map(l => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* TRUST STRIP — Built by entrepreneurs */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="rounded-3xl bg-slate-50 p-8 md:p-12 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-2">
            <span className="dv-pill mb-3"><Heart size={13} className="text-rose-500" /> Built by entrepreneurs</span>
            <h2 className="dv-display text-2xl md:text-3xl mb-3">Created from real business experience.</h2>
            <p className="text-slate-600">DealVest exists because too many good businesses die before they become great. Read our founder's story.</p>
            <Link to="/founder" className="dv-btn dv-btn-secondary dv-btn-sm mt-5" data-testid="home-founder-link">Read the founder's story <ArrowRight size={14} /></Link>
          </div>
          <div className="lg:col-span-3 grid grid-cols-3 gap-3">
            {[
              { v: "20+", l: "Years of operator experience" },
              { v: "24+", l: "Companies built by our founder" },
              { v: "700+", l: "People employed" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-600">{s.v}</div>
                <div className="text-xs md:text-sm text-slate-600 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP — Final CTA */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 p-8 md:p-14 text-white text-center">
          <h2 className="dv-display text-3xl md:text-4xl mb-4">Built for African and Middle Eastern markets.</h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">11 countries. Verified opportunities. AI-powered matching. Bank-grade confidentiality.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            <div><div className="text-3xl md:text-4xl font-bold">11</div><div className="text-sm text-blue-200 mt-1">Countries</div></div>
            <div><div className="text-3xl md:text-4xl font-bold">{(stats.businesses_listed || 1200).toLocaleString()}+</div><div className="text-sm text-blue-200 mt-1">Listings</div></div>
            <div><div className="text-3xl md:text-4xl font-bold">{(stats.investors_registered || 800).toLocaleString()}+</div><div className="text-sm text-blue-200 mt-1">Investors</div></div>
            <div><div className="text-3xl md:text-4xl font-bold">$285M+</div><div className="text-sm text-blue-200 mt-1">Capital</div></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <Link to="/signup" className="dv-btn bg-white text-blue-600 hover:bg-blue-50">Get started free</Link>
            <Link to="/marketplace" className="dv-btn bg-white/10 hover:bg-white/20 text-white border border-white/25">Browse marketplace</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
